import React from 'react';
import { Link } from 'react-router-dom';

function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <nav className="text-sm text-dark-text-secondary mb-6">
        <Link to="/" className="hover:text-dark-text transition-colors">Home</Link>
        <span className="mx-2">›</span>
        <span className="text-dark-text">Privacy Policy</span>
      </nav>

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        Privacy Policy
      </h1>

      <p className="text-sm text-dark-text-secondary mb-8">
        Last updated: February 2025
      </p>

      {/* Content */}
      <div className="prose prose-invert prose-zinc max-w-none space-y-8">
        {/* Section 1 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">1. Data Controller</h2>
          <p className="text-zinc-400 leading-relaxed">
            The data controller for DayTradingBench is:
          </p>
          <div className="mt-4 p-4 bg-dark-tertiary border border-dark-border rounded-lg">
            <p className="text-zinc-300 font-medium">daytradingbench.com</p>
            <p className="text-zinc-400 text-sm mt-2">
              Email:{' '}
              <a
                href="mailto:contact@daytradingbench.com"
                className="text-green-500 hover:text-green-400"
              >
                contact@daytradingbench.com
              </a>
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">2. Information We Collect</h2>
          <p className="text-zinc-400 leading-relaxed">
            We collect minimal information to operate the Service:
          </p>
          <ul className="list-disc list-inside text-zinc-400 leading-relaxed mt-3 space-y-2">
            <li><strong className="text-zinc-300">Log data:</strong> IP address, browser type, pages visited, time and date of visit, and other standard web server logs</li>
            <li><strong className="text-zinc-300">Usage data:</strong> How you interact with the Service, including pages viewed and features used</li>
            <li><strong className="text-zinc-300">Device information:</strong> Device type, operating system, and screen resolution</li>
          </ul>
          <p className="text-zinc-400 leading-relaxed mt-3">
            DayTradingBench currently operates without user accounts. We do not collect personal identification information such as names, email addresses, or payment details unless you contact us directly.
          </p>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">3. How We Use Information</h2>
          <p className="text-zinc-400 leading-relaxed">
            We use collected information to:
          </p>
          <ul className="list-disc list-inside text-zinc-400 leading-relaxed mt-3 space-y-2">
            <li>Operate and maintain the Service</li>
            <li>Analyze usage patterns to improve the Service</li>
            <li>Monitor for security threats and abuse</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">4. Cookies</h2>
          <p className="text-zinc-400 leading-relaxed">
            DayTradingBench does not use cookies. Our analytics solution (Umami) is cookie-free and does not track individual users.
          </p>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">5. Third-Party Services</h2>
          <p className="text-zinc-400 leading-relaxed">
            We may use third-party services that collect information used to identify you:
          </p>
          <ul className="list-disc list-inside text-zinc-400 leading-relaxed mt-3 space-y-2">
            <li><strong className="text-zinc-300">Hosting providers:</strong> For serving the website and storing data</li>
            <li><strong className="text-zinc-300">Analytics services:</strong> To analyze traffic and usage patterns</li>
            <li><strong className="text-zinc-300">CDN providers:</strong> For content delivery and performance optimization</li>
          </ul>
          <p className="text-zinc-400 leading-relaxed mt-3">
            These third parties have their own privacy policies governing the use of your information.
          </p>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">6. Data Retention</h2>
          <p className="text-zinc-400 leading-relaxed">
            We retain log data and usage information for a reasonable period necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            Server logs are typically retained for 90 days, after which they are automatically deleted.
          </p>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">7. Your Rights (GDPR)</h2>
          <p className="text-zinc-400 leading-relaxed">
            Under the General Data Protection Regulation (GDPR), if you are a resident of the European Economic Area (EEA), you have the following rights:
          </p>
          <ul className="list-disc list-inside text-zinc-400 leading-relaxed mt-3 space-y-2">
            <li><strong className="text-zinc-300">Right of access:</strong> Request copies of your personal data</li>
            <li><strong className="text-zinc-300">Right to rectification:</strong> Request correction of inaccurate data</li>
            <li><strong className="text-zinc-300">Right to erasure:</strong> Request deletion of your personal data</li>
            <li><strong className="text-zinc-300">Right to restrict processing:</strong> Request limitation of data processing</li>
            <li><strong className="text-zinc-300">Right to data portability:</strong> Request transfer of your data</li>
            <li><strong className="text-zinc-300">Right to object:</strong> Object to processing of your personal data</li>
          </ul>
          <p className="text-zinc-400 leading-relaxed mt-3">
            To exercise any of these rights, please contact us at{' '}
            <a
              href="mailto:contact@daytradingbench.com"
              className="text-green-500 hover:text-green-400"
            >
              contact@daytradingbench.com
            </a>.
          </p>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">8. Data Security</h2>
          <p className="text-zinc-400 leading-relaxed">
            We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul className="list-disc list-inside text-zinc-400 leading-relaxed mt-3 space-y-2">
            <li>HTTPS encryption for all data transmission</li>
            <li>Regular security updates and patches</li>
            <li>Access controls and authentication</li>
            <li>Regular security monitoring</li>
          </ul>
          <p className="text-zinc-400 leading-relaxed mt-3">
            However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        {/* Section 9 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">9. Changes to This Policy</h2>
          <p className="text-zinc-400 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            We encourage you to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
          </p>
        </section>

        {/* Section 10 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">10. Contact Information</h2>
          <p className="text-zinc-400 leading-relaxed">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="mt-4 p-4 bg-dark-tertiary border border-dark-border rounded-lg">
            <p className="text-zinc-300 font-medium">daytradingbench.com</p>
            <p className="text-zinc-400 text-sm mt-2">
              Email:{' '}
              <a
                href="mailto:contact@daytradingbench.com"
                className="text-green-500 hover:text-green-400"
              >
                contact@daytradingbench.com
              </a>
            </p>
          </div>
        </section>
      </div>

      {/* Back link */}
      <div className="mt-12 pt-8 border-t border-dark-border">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-dark-text-secondary hover:text-dark-text transition-colors"
        >
          <span>←</span>
          <span>Back to Leaderboard</span>
        </Link>
      </div>
    </div>
  );
}

export default PrivacyPage;
